import { Injectable } from '@angular/core';

import { Dietitian } from '../../data/dietitian/domain/dietitian';
import { MessageHelpEventProvider } from '../../data/help-me/domain/events/message-help/message-help-event_provider';
import {
  MessageHelpClosed,
  MessageHelpCreated,
  MessageHelpUpdated,
} from '../../data/help-me/domain/events/message-help/message-help-events';
import { FaqHelp } from '../../data/help-me/domain/faq-help';
import {
  MessageHelp,
  MessageThreadHelpProps,
} from '../../data/help-me/domain/message-help';
import { UrlTargeting } from '../../data/help-me/domain/url-targeting';
import { FaqHelpRepository } from '../../data/help-me/repositories/faq-help_repository';
import { MessageHelpRepository } from '../../data/help-me/repositories/message-help_repository';
import { UrlTargetingRepository } from '../../data/help-me/repositories/url-targeting_repository';

@Injectable()
export class HelpMeFacade {
  constructor(
    private faqHelpRepository: FaqHelpRepository,
    private messageHelpRepository: MessageHelpRepository,
    private urlTargetingRepository: UrlTargetingRepository,
    private messageHelpEventProvider: MessageHelpEventProvider,
  ) {
    // Nada
  }

  get messageHelpEventProvider$() {
    return this.messageHelpEventProvider.events$;
  }

  async loadUrlTargeting(): Promise<UrlTargeting[]> {
    return this.urlTargetingRepository.findAll();
  }

  async loadFaqHelp(target: string): Promise<FaqHelp[]> {
    try {
      return this.faqHelpRepository.findAllByTarget(target);
    } catch (e) {
      console.error('🔴 loadBasicHelp', e);
    }
    return [];
  }

  async postMessage(messageHelp: MessageHelp) {
    if (messageHelp.attachments) {
      for (const a of messageHelp.attachments) {
        a.path = HelpMeFacade.getDietitianMessageAttachmentPath(
          messageHelp.dietitianId,
          messageHelp.id.toString(),
          a.name,
        );
        a.status = 'started';
        a.upload = this.messageHelpRepository.uploadAttachement(a);
        a.progress = a.upload.progress;
        a.url = await a.upload.getUrl();
        a.status = 'completed';
      }
    }
    try {
      await this.messageHelpRepository.create(messageHelp);
      this.messageHelpEventProvider.dispatch(
        new MessageHelpCreated(messageHelp, true),
      );
    } catch (e) {
      console.error('🔴 postMessage', e);
      throw e;
    }
  }

  async updateMessage(messageHelp: MessageHelp) {
    try {
      await this.messageHelpRepository.update(messageHelp);
      this.messageHelpEventProvider.dispatch(
        new MessageHelpUpdated(messageHelp, true),
      );
    } catch (e) {
      console.error('🔴 updateMessage', e);
      throw e;
    }
  }

  async postReply(messageHelp: MessageHelp, reply: MessageThreadHelpProps) {
    if (reply.attachments) {
      for (const ra of reply.attachments) {
        ra.path = HelpMeFacade.getDietitianMessageAttachmentPath(
          messageHelp.dietitianId,
          messageHelp.id.toString(),
          ra.name,
        );
        ra.status = 'started';
        ra.upload = this.messageHelpRepository.uploadAttachement(ra);
        ra.progress = ra.upload.progress;
        ra.url = await ra.upload.getUrl();
        ra.status = 'completed';
      }
    }
    if (!messageHelp.thread) {
      messageHelp.thread = [];
    }
    messageHelp.thread?.push(reply);
    try {
      await this.messageHelpRepository.update(messageHelp);
      this.messageHelpEventProvider.dispatch(
        new MessageHelpUpdated(messageHelp, true),
      );
    } catch (e) {
      console.error('🔴 postMessage', e);
      throw e;
    }
  }

  async loadMessage(dietitian: Dietitian | undefined) {
    return this.messageHelpRepository.findAll(dietitian?.id.toString());
  }

  public static getDietitianMessageAttachmentPath(
    dietitianId: string | undefined,
    messageUUId: string,
    filename: string,
  ): string {
    return `users/${
      dietitianId ?? '_unknown_'
    }/attachments/${messageUUId}-${filename}`;
  }

  async closeMessageHelp(messageHelp: MessageHelp) {
    messageHelp.closed = true;
    try {
      await this.messageHelpRepository.update(messageHelp);
      this.messageHelpEventProvider.dispatch(
        new MessageHelpClosed(messageHelp, true),
      );
    } catch (e) {
      messageHelp.closed = false;
      console.error('🔴 postMessage', e);
      throw e;
    }
  }
}
